import React, { useState, useEffect } from 'react';
import {
    Row,
    Col,
    Card,
    Table,
    Badge,
    Alert,
    Spinner
} from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import PaginationComponent from '../common/Pagination';
import { getPreviousStatusVariant } from './BRCHelper';
import instance from "../../services/baseService";
import { PER_PAGE } from '../../constants/user'
const PreviousPeriodTab = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [previousPeriodData, setPreviousPeriodData] = useState([]);

    // Loading States
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    // UI States
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const { client_id, report_id } = useParams();

    // Transform API data to match component structure
    const transformApiData = (apiData) => {
        return apiData.map(item => ({
            id: item.id,
            sbNo1: item.sbNo1,
            sbDate1: item.sbDate1,
            portCode1: item.portCode1,
            rosctelAmount: item.rosctelAmount1,
            rodTepAmount: item.rodTepAmount1,
            sbNo2: item.sbNo2,
            sbDate2: item.sbDate2,
            portCode2: item.portCode2,
            status: item.status_code
        }));
    };

    // Data transformation functions
    const formatCurrency = (amount) => {
        if (!amount || amount === null || amount === undefined) return '₹0';
        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        return `₹${numAmount.toLocaleString('en-IN')}`;
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === null) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-IN');
        } catch (error) {
            return '-';
        }
    };

    const formatDisplayValue = (value, fallback = '-') => {
        if (value === null || value === undefined || value === '') return fallback;
        return value;
    };

    // API Functions
    const fetchPreviousPeriodData = async (page = 1, showLoader = true) => {
        if (showLoader) {
            setLoading(true);
        }
        setError('');

        try {
            // Make actual API call
            const res = await instance.post(
                `/api/v1/reconciliation/view/previous/report/${client_id}/${report_id}`,
                {
                    page: page,
                    perpage: PER_PAGE
                }
            );

            if (res.data && res.data.type === 'success') {
                const responseData = res.data.data;
                const transformedData = transformApiData(responseData.lists || []);

                setPreviousPeriodData(transformedData);
                setTotalCount(responseData.total_count || 0);
                setCurrentPage(responseData.page || page);
            } else {
                throw new Error(res.data?.message || 'Failed to fetch previous period data');
            }

        } catch (err) {
            console.error('Fetch Previous Period Data Error:', err);

            // Handle different error scenarios
            if (err.response) {
                const { status, data } = err.response;
                if (status === 404) {
                    setError('Previous period data not found. Please check if the data exists.');
                } else if (status === 403) {
                    setError('Access denied. You don\'t have permission to view previous period data.');
                } else if (status >= 500) {
                    setError('Server error occurred. Please try again later.');
                } else {
                    setError(data?.message || 'Failed to fetch previous period data. Please try again.');
                }
            } else if (err.request) {
                setError('Network error. Please check your connection and try again.');
            } else {
                setError(err.message || 'Failed to fetch previous period data. Please try again.');
            }

            setPreviousPeriodData([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    const handlePagination = async (page) => {
        setCurrentPage(page);
        await fetchPreviousPeriodData(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Load initial data
    useEffect(() => {
        if (client_id && report_id) {
            fetchPreviousPeriodData(1);
        } else {
            setError('Client ID and Upload ID are required to fetch previous period data.');
            setInitialLoading(false);
        }
    }, [client_id, report_id]);

    // Auto-clear success message
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    const clearMessages = () => {
        setError('');
        setSuccess('');
    };

    return (
        <div>
            {/* Header */}
            <Row className="align-items-center justify-content-between mb-4">
                <Col>
                    <h4 className="fw-bold mb-0">Previous Period Data</h4>
                    <p className="text-muted mb-0">Review and manage previous period data.</p>
                </Col>
            </Row>

            {/* Success Alert */}
            {success && (
                <Alert variant="success" dismissible onClose={clearMessages} className="mb-3">
                    {success}
                </Alert>
            )}

            {/* Error Alert */}
            {error && (
                <Alert variant="danger" dismissible onClose={clearMessages} className="mb-3">
                    {error}
                </Alert>
            )}

            {/* Main Content Card */}
            <Card className="border-0">
                <Card.Body>
                    {initialLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" role="status" className="mb-3">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                            <p className="text-muted">Loading previous period data...</p>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <Table hover className="core-ui-table">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-left" scope="col">SB NO</th>
                                            <th className="text-left" scope="col">SB DATE</th>
                                            <th className="text-left" scope="col">PORT CODE</th>
                                            <th className="text-left" scope="col">ROSCTEL AMOUNT</th>
                                            <th className="text-left" scope="col">RODTEP AMOUNT</th>
                                            <th className="text-left" scope="col">SB NO</th>
                                            <th className="text-left" scope="col">SB DATE</th>
                                            <th className="text-left" scope="col">PORT CODE</th>
                                            <th className='text-center' scope="col">STATUS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading && previousPeriodData.length > 0 && (
                                            <tr>
                                                <td colSpan="9" className="text-center py-2">
                                                    <small className="text-muted">
                                                        <Spinner animation="border" size="sm" className="me-2" />
                                                        Updating records...
                                                    </small>
                                                </td>
                                            </tr>
                                        )}

                                        {previousPeriodData.length > 0 ? (
                                            previousPeriodData.map((item) => (
                                                <tr key={item.id}>
                                                    <td className="text-left">{formatDisplayValue(item.sbNo1)}</td>
                                                    <td className="text-left">{formatDate(item.sbDate1)}</td>
                                                    <td className="text-left">{formatDisplayValue(item.portCode1)}</td>
                                                    <td className="text-left">{formatCurrency(item.rosctelAmount)}</td>
                                                    <td className="text-left">{formatCurrency(item.rodTepAmount)}</td>
                                                    <td className="text-left">{formatDisplayValue(item.sbNo2)}</td>
                                                    <td className="text-left">{formatDate(item.sbDate2)}</td>
                                                    <td className="text-left">{formatDisplayValue(item.portCode2)}</td>
                                                    <td className="text-center">
                                                        <Badge
                                                            bg={getPreviousStatusVariant(item.status)}
                                                            className="rounded-pill"
                                                        >
                                                            {item.status}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="text-center py-4 text-muted">
                                                    {error ? 'Unable to load previous period data' : 'No previous period data found'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalCount > PER_PAGE && (
                                <div className="mt-3">
                                    <PaginationComponent
                                        total_count={totalCount}
                                        current_page={currentPage}
                                        per_page={PER_PAGE}
                                        handleOnClick={handlePagination}
                                    />
                                </div>
                            )}

                            {/* Data Summary */}
                            {!loading && previousPeriodData.length > 0 && (
                                <div className="mt-3 text-muted small">
                                    Showing {previousPeriodData.length} of {totalCount} records
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default PreviousPeriodTab;